<template>
  <Modal 
    :visible="visible" 
    title="标记注单为异常"
    maxWidth="lg"
    :close-on-backdrop="!submitting"
    @close="handleClose"
  >
    <div v-if="betId" class="mb-4 p-2">
      <div class="flex items-center mb-4">
        <div class="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-red-100 mr-4">
          <svg class="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <p class="text-sm text-gray-500">
          您正在将注单 <span class="font-medium">{{ betId }}</span> 标记为异常注单。
        </p>
      </div>

      <div class="mb-4">
        <label for="abnormal_reason" class="block text-sm font-medium text-gray-700 mb-1">异常原因</label>
        <select 
          v-model="reason" 
          id="abnormal_reason" 
          class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
          :disabled="submitting"
        >
          <option value="">请选择异常原因</option>
          <option value="system_error">系统错误</option>
          <option value="amount_error">金额错误</option>
          <option value="betting_anomaly">投注异常</option>
          <option value="other_issue">其他问题</option>
        </select>
      </div>

      <div class="mb-4">
        <label for="abnormal_description" class="block text-sm font-medium text-gray-700 mb-1">详细描述</label>
        <textarea 
          v-model="description" 
          id="abnormal_description" 
          rows="3" 
          class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm" 
          placeholder="请提供更详细的异常描述..."
          :disabled="submitting"
        ></textarea>
      </div>
      
      <div v-if="error" class="mt-2 text-sm text-red-600">{{ error }}</div>
    </div>

    <template #footer>
      <button 
        @click="handleClose" 
        :disabled="submitting" 
        type="button" 
        class="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto sm:text-sm mr-3"
        :class="{ 'opacity-75 cursor-not-allowed': submitting }"
      >
        取消
      </button>
      <button 
        @click="submit" 
        :disabled="submitting || !reason" 
        type="button" 
        class="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:w-auto sm:text-sm"
        :class="{ 'opacity-75 cursor-not-allowed': submitting || !reason }"
      >
        <template v-if="submitting">
          <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          提交中...
        </template>
        <template v-else>确认标记</template>
      </button>
    </template>
  </Modal>
</template>

<script setup>
import { ref, watch } from 'vue';
import Modal from '@/components/common/Modal.vue';
import AdminApiClient from '@/utils/admin-api-client';

const apiClient = new AdminApiClient();

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  betId: {
    type: [String, Number],
    default: ''
  },
  system: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['close', 'submit-success']);

// 状态变量
const reason = ref('');
const description = ref('');
const error = ref('');
const submitting = ref(false);

// 监听可见性变化
watch(() => props.visible, (newVal) => {
  if (newVal) {
    resetForm();
  }
});

// 重置表单
function resetForm() {
  reason.value = '';
  description.value = '';
  error.value = '';
  submitting.value = false;
}

// 关闭模态框
function handleClose() {
  if (submitting.value) return;
  emit('close');
}

// 提交标记异常请求
async function submit() {
  if (!reason.value) {
    error.value = '请选择异常原因';
    return;
  }
  
  if (!props.betId || !props.system) {
    error.value = '缺少注单ID或系统信息';
    return;
  }
  
  submitting.value = true;
  error.value = '';
  
  try {
    // 构建请求数据
    const formData = {
      fundsn: props.betId,
      system: props.system,
      reason: reason.value,
      description: description.value
    };
    
    // 使用API客户端标记异常
    const response = await apiClient.markAbnormalBet(formData);
    
    // 处理响应
    if (response && response.success) {
      // 通知父组件标记成功
      emit('submit-success', {
        abnormal_id: response.data.abnormal_id,
        id: props.betId,
        reason: reason.value,
        description: description.value
      });
      
      // 关闭模态框
      emit('close');
      
      // 显示成功提示
      alert('标记异常成功');
    } else {
      error.value = response?.message || '标记失败';
    }
  } catch (e) {
    console.error('标记异常注单出错:', e);
    error.value = e.message || '标记失败，请稍后重试';
  } finally {
    submitting.value = false;
  }
}
</script> 